import React, { useEffect, useRef, useState } from "react";
import DSDropdown from "./Dropdown";
import DSSearchInput from "./SearchInput";
import { Search } from "lucide-react";

const BEFORE_SEARCH_TEXT = "검색어를 입력해주세요.";
const EMPTY_RESULT_TEXT = "검색 결과가 존재하지 않습니다.";

const DotsLoader = () => (
  <div className="ds-search-dropdown__dots">
    <span />
    <span />
    <span />
    <span />
  </div>
);
const SCROLL_BOTTOM_THRESHOLD = 4;

const cacheKeyOf = (field, term) => `${field}\u0000${term}`;

/**
 * @param {(term, page, pageSize, field) => Promise<{list, hasMore}>} onSearch - 한 페이지 검색. field 는 fields 를 쓸 때 선택된 값
 * @param {(results, selectItem) => ReactNode} children - 결과 리스트 마크업. 항목 클릭 시 selectItem(item) 호출
 * @param {(item) => void} onSelectItem - 항목 선택 콜백
 * @param {string} [placeholder] - 안내문구
 * @param {Array<{value: string, text: string, placeholder?: string}>} [fields] - 검색 필드 선택지. 검색창 왼쪽에 드롭다운이 붙고 첫 항목이 기본값
 * @param {number} [pageSize] - 페이지당 개수 (기본 30)
 * @param {boolean} open - 열림 여부 (부모가 관리)
 * @param {(open) => void} onOpenChange - 열림/닫힘 변경 콜백
 * @param {string} [className] - wrapper 추가 클래스
 * @param {boolean} [disabled] - true 면 트리거 클릭 불가
 */
const DSSearchDropdown = ({
  onSearch,
  fields,
  children,
  onSelectItem,
  placeholder,
  pageSize = 30,
  open,
  onOpenChange,
  className = "",
  disabled = false,
}) => {
  const [value, setValue] = useState("");
  const [field, setField] = useState(fields?.[0]?.value);
  const [searchedTerm, setSearchedTerm] = useState(null);
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const cacheRef = useRef({});
  const searchedTermRef = useRef(null);
  const fieldRef = useRef(field);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onOpenChange]);

  const handleSubmit = () => {
    const term = value.trim();
    if (!term) return;
    setSearchedTerm(term);
    searchedTermRef.current = term;
    runSearch(term, 1, false);
  };

  const runSearch = async (term, pageNum, append) => {
    const searchField = fieldRef.current;
    const cacheKey = cacheKeyOf(searchField, term);
    if (cacheRef.current[cacheKey]?.[pageNum]) {
      applyPage(cacheKey, pageNum, append);
      return;
    }
    (append ? setLoadingMore : setLoading)(true);
    try {
      const { list, hasMore: pageHasMore } = await onSearch(
        term,
        pageNum,
        pageSize,
        searchField,
      );
      cacheRef.current = {
        ...cacheRef.current,
        [cacheKey]: {
          ...(cacheRef.current[cacheKey] || {}),
          [pageNum]: { list, hasMore: pageHasMore },
        },
      };
      if (term !== searchedTermRef.current || searchField !== fieldRef.current) {
        return;
      }
      applyPage(cacheKey, pageNum, append);
    } finally {
      if (term === searchedTermRef.current && searchField === fieldRef.current) {
        (append ? setLoadingMore : setLoading)(false);
      }
    }
  };

  const applyPage = (cacheKey, pageNum, append) => {
    const cachedPage = cacheRef.current[cacheKey]?.[pageNum];
    setResults((prev) =>
      append ? [...prev, ...(cachedPage?.list || [])] : cachedPage?.list || [],
    );
    setHasMore(cachedPage?.hasMore || false);
    setPage(pageNum);
  };

  const handleFieldChange = (next) => {
    setField(next);
    fieldRef.current = next;
    resetResults();
  };

  const handleSelect = (item) => {
    onSelectItem(item);
    setValue("");
    resetResults();
    onOpenChange(false);
  };

  const resetResults = () => {
    setSearchedTerm(null);
    searchedTermRef.current = null;
    setResults([]);
    setHasMore(false);
    setPage(1);
  };

  const handlePanelScroll = (e) => {
    if (loading || loadingMore || !hasMore || !searchedTerm) return;
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD) {
      runSearch(searchedTerm, page + 1, true);
    }
  };

  const inputPlaceholder =
    fields?.find((f) => f.value === field)?.placeholder ?? placeholder;

  return (
    <div className={`ds-search-dropdown ${className}`} ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        className="ds-search-dropdown__trigger"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onOpenChange(!open)}
      >
        <Search size={16} className="ds-search-dropdown__trigger-icon" />
        <span className="ds-search-dropdown__trigger-placeholder">
          {placeholder}
        </span>
      </button>
      {open && (
        <div className="ds-search-dropdown__flyout">
          <div className="ds-search-dropdown__search">
            {fields ? (
              <div onMouseDown={(e) => e.preventDefault()}>
                <DSDropdown
                  className="ds-search-dropdown__field"
                  options={fields}
                  value={field}
                  onChange={handleFieldChange}
                />
              </div>
            ) : null}
            <DSSearchInput
              className="ds-search-dropdown__input"
              value={value}
              onChange={setValue}
              onSubmit={handleSubmit}
              onBlur={() => !value.trim() && onOpenChange(false)}
              placeholder={inputPlaceholder}
              autoFocus
            />
          </div>
          <div
            className="ds-search-dropdown__panel"
            onScroll={handlePanelScroll}
          >
            {searchedTerm === null ? (
              <div className="ds-search-dropdown__empty">
                {BEFORE_SEARCH_TEXT}
              </div>
            ) : loading ? (
              <div className="ds-search-dropdown__empty">
                <DotsLoader />
              </div>
            ) : results.length > 0 ? (
              children(results, handleSelect)
            ) : (
              <div className="ds-search-dropdown__empty">{EMPTY_RESULT_TEXT}</div>
            )}
          </div>
          <div className="ds-search-dropdown__hint">
            <span className="ds-search-dropdown__hint-key">Enter</span>
            를 누르고 검색
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(DSSearchDropdown);
